import { EMPTY_VALUE } from "staticConfiguration";
import type { MenuItemType } from "core/entities";
import { useCentrixApi } from "core/hooks/useCentrixApi";
import { useLocale } from "core/hooks/useLocale";
import { useMemo } from "react";
import type { CategoryMenuItem, MenuLink, RootMenuItem } from "./MobileNavDefs";

export const URL_UNDEFINED_CHARACTER = "#";

/**
 * @param menuItemType defaults to "Header"
 */
export function useMobileMenuItems(menuItemType: MenuItemType = "Header") {
  const locale = useLocale();

  const { data, isLoading, isError } = useCentrixApi({
    path: "/api/app/menu-item/v2",
    parameters: {
      query: {
        locale,
      },
    },
    swrOptions: {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  });

  const menuItems: RootMenuItem[] = useMemo(() => {
    if (!data || !Array.isArray(data)) {
      return [];
    }
    const rootItems: RootMenuItem[] = data
      .filter((item) => {
        const isCorrectType = item.menuItemType === menuItemType;
        const shouldDisplay =
          item.menuItemType !== "Header" ||
          (Array.isArray(item.docRootNodes) && item.docRootNodes.length > 0) ||
          (Array.isArray(item?.submenus) && item.submenus.length > 0) ||
          item.url !== "#";
        return isCorrectType && shouldDisplay;
      })
      .map((rootItem) => {
        const libraries: RootMenuItem["libraries"] =
          rootItem.docRootNodes
            ?.map((entry) => ({
              id: entry.id,
              path: entry.path,
              displayName: entry.name,
            }))
            .filter(
              (entry): entry is RootMenuItem["libraries"][number] =>
                typeof entry.id === "string" &&
                typeof entry.path === "string" &&
                typeof entry.displayName === "string"
            ) || [];
        const categories: RootMenuItem["categories"] =
          rootItem.submenus
            ?.map((entry) => {
              const links: RootMenuItem["categories"][number]["links"] =
                entry.submenus
                  ?.map((link) => ({
                    id: link.id ?? EMPTY_VALUE,
                    displayName: link.displayName ?? EMPTY_VALUE,
                    url: link.url,
                    target: link.target,
                  }))
                  .filter(
                    (linkEntry): linkEntry is MenuLink =>
                      typeof entry.id === "string"
                  ) || [];
              return {
                id: entry.id,
                displayName: entry.displayName,
                url:
                  entry.url === URL_UNDEFINED_CHARACTER ? undefined : entry.url,
                links,
                target: entry.target,
              };
            })
            .filter(
              (entry): entry is CategoryMenuItem =>
                typeof entry.id === "string" &&
                ((entry.links?.length ?? 0) > 0 || Boolean(entry.url))
            ) || [];
        return {
          id: rootItem.id ?? EMPTY_VALUE,
          libraries,
          displayName: rootItem.displayName ?? EMPTY_VALUE,
          target: rootItem.target,
          url:
            rootItem.url === URL_UNDEFINED_CHARACTER ? undefined : rootItem.url,
          categories,
        };
      });
    return rootItems;
  }, [data, menuItemType]);

  return { isLoading, isError, data, menuItems };
}
